import {
  TipoAtendimentoEnum,
  TipoAtendimentoProfissional,
  TipoConsultaOdontoEnum,
  TipoEstabelecimentoEnum,
} from 'graphql/types.generated'
import {
  ProcedimentosAutomaticoAtendimentoOdonto,
  ProcedimentosAutomaticoAtendimentoPoliclinica,
  ProcedimentosAutomaticoAtendimentoUpa,
  ProcedimentosAutomaticosAtendimentoIndividual,
  ProcedimentosAutomaticosEscutaInicial,
} from 'types/enums'
import { isCboDentista, isCboDentistaClinicoGeral, isCboNivelSuperior } from 'types/NivelCbo'
import { isCboMedico } from 'util/atendimento/cboHelper'
import { isUndefinedOrNull } from 'util/checks'
import { v4 as uuidv4 } from 'uuid'

import { ProcedimentoAutomatico } from '../model-atendimento'
import { ProcedimentoModel } from '../model-procedimento'

export interface CalculateProcedimentoAutomaticoToAddProps {
  tipoAtendimentoProfissional: TipoAtendimentoProfissional
  tipoEstabelecimento: TipoEstabelecimentoEnum
  tipoAtendimento?: TipoAtendimentoEnum
  cbo2002: string
  isProfResponsavelObservacao: boolean
  tipoConsulta?: TipoConsultaOdontoEnum | null
}

export const calculateProcedimentoAutomaticoToAdd = (props: CalculateProcedimentoAutomaticoToAddProps) => {
  const {
    tipoAtendimentoProfissional,
    tipoEstabelecimento,
    tipoAtendimento,
    cbo2002,
    isProfResponsavelObservacao,
    tipoConsulta,
  } = props
  switch (tipoAtendimentoProfissional) {
    case TipoAtendimentoProfissional.ATENDIMENTO_ODONTOLOGICO: {
      return calculateProcedimentoAutomaticoOdontologico(tipoEstabelecimento, tipoAtendimento, tipoConsulta, cbo2002)
    }
    default: {
      return calculateProcedimentoAutomaticoIndividual(
        tipoEstabelecimento,
        tipoAtendimento,
        cbo2002,
        isProfResponsavelObservacao
      )
    }
  }
}

export const calculateProcedimentoAutomaticoIndividual = (
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  tipoAtendimento: TipoAtendimentoEnum | undefined,
  cbo2002: string,
  isProfResponsavelObservacao: boolean
) => {
  const isMedico = isCboMedico(cbo2002)
  const isProfNivelSuperior = isCboNivelSuperior(cbo2002)

  if (isUpaComObservacao(tipoEstabelecimento, isProfResponsavelObservacao)) {
    return ProcedimentosAutomaticoAtendimentoUpa.ATENDIMENTO_URGENCIA_COM_OBSERVACAO_AE
  }

  if (isUndefinedOrNull(tipoAtendimento)) {
    return null
  }

  const isTipoAtendimentoUrgencia = tipoAtendimento === TipoAtendimentoEnum.URGENCIA
  const isTipoAtendimentoConsulta =
    tipoAtendimento === TipoAtendimentoEnum.CONSULTA_NO_DIA ||
    tipoAtendimento === TipoAtendimentoEnum.CONSULTA ||
    tipoAtendimento === TipoAtendimentoEnum.CONSULTA_AGENDADA_CUIDADO_CONTINUADO

  switch (tipoEstabelecimento) {
    case TipoEstabelecimentoEnum.UBS:
      return procedAutomaticoUbs(isMedico, isTipoAtendimentoUrgencia)
    case TipoEstabelecimentoEnum.POLICLINICA:
      return procedAutomaticoPoliclinicaIndividual(isMedico, isTipoAtendimentoUrgencia)
    case TipoEstabelecimentoEnum.UPA:
      return procedAutomaticoUpaIndividual(
        isTipoAtendimentoConsulta,
        isTipoAtendimentoUrgencia,
        isMedico,
        isProfNivelSuperior
      )
    default:
      return null
  }
}

const procedAutomaticoUbs = (isMedico: boolean, isTipoAtendimentoUrgencia: boolean) => {
  return isTipoAtendimentoUrgencia
    ? isMedico
      ? ProcedimentosAutomaticosAtendimentoIndividual.ATENDIMENTO_DE_URGENCIA_NA_ATENCAO_BASICA
      : null
    : isMedico
    ? ProcedimentosAutomaticosAtendimentoIndividual.CONSULTA_MEDICA_EM_ATENCAO_BASICA
    : ProcedimentosAutomaticosAtendimentoIndividual.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_BASICA
}

const isUpaComObservacao = (tipoEstabelecimento: TipoEstabelecimentoEnum, isProfResponsavelObservacao: boolean) =>
  tipoEstabelecimento === TipoEstabelecimentoEnum.UPA && isProfResponsavelObservacao

const procedAutomaticoPoliclinicaIndividual = (
  isMedico: boolean,
  isTipoAtendimentoUrgencia: boolean
): ProcedimentosAutomaticoAtendimentoPoliclinica | null => {
  if (isMedico) {
    return isTipoAtendimentoUrgencia
      ? ProcedimentosAutomaticoAtendimentoPoliclinica.ATENDIMENTO_DE_URGENCIA_EM_ATENCAO_ESPECIALIZADA
      : ProcedimentosAutomaticoAtendimentoPoliclinica.CONSULTA_MEDICA_EM_ATENCAO_ESPECIALIZADA
  }
  if (!isTipoAtendimentoUrgencia) {
    return ProcedimentosAutomaticoAtendimentoPoliclinica.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_ESPECIALIZADA
  }
  return null
}

const procedAutomaticoUpaIndividual = (
  isTipoAtendimentoConsulta: boolean,
  isTipoAtendimentoUrgencia: boolean,
  isMedico: boolean,
  isProfNivelSuperior: boolean
): ProcedimentosAutomaticoAtendimentoUpa | ProcedimentosAutomaticoAtendimentoPoliclinica | null => {
  if (isTipoAtendimentoConsulta) {
    if (isMedico) return ProcedimentosAutomaticoAtendimentoUpa.ATENDIMENTO_MEDICO_UPA
    if (isProfNivelSuperior) {
      return ProcedimentosAutomaticoAtendimentoPoliclinica.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_ESPECIALIZADA
    }
    return null
  }
  if (isTipoAtendimentoUrgencia && isMedico) {
    return ProcedimentosAutomaticoAtendimentoPoliclinica.ATENDIMENTO_DE_URGENCIA_EM_ATENCAO_ESPECIALIZADA
  }
  return null
}

export const calculateProcedimentoAutomaticoOdontologico = (
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  tipoAtendimento: TipoAtendimentoEnum | undefined,
  tipoConsulta: TipoConsultaOdontoEnum | null | undefined,
  cbo2002: string
) => {
  if (tipoConsulta === TipoConsultaOdontoEnum.PRIMEIRA_CONSULTA_ODONTOLOGICA_PROGRAMATICA) {
    return ProcedimentosAutomaticoAtendimentoOdonto.PRIMEIRA_CONSULTA_ODONTOLOGICA_PROGRAMATICA
  }

  if (isUndefinedOrNull(tipoAtendimento)) {
    return null
  }

  const isDentista = isCboDentista(cbo2002)
  const isDentistaClinicoGeral = isCboDentistaClinicoGeral(cbo2002)
  const isTipoAtendimentoUrgencia = tipoAtendimento === TipoAtendimentoEnum.URGENCIA
  const isTipoAtendimentoConsulta =
    tipoAtendimento === TipoAtendimentoEnum.CONSULTA_NO_DIA ||
    tipoAtendimento === TipoAtendimentoEnum.CONSULTA ||
    tipoAtendimento === TipoAtendimentoEnum.CONSULTA_AGENDADA_CUIDADO_CONTINUADO

  switch (tipoEstabelecimento) {
    case TipoEstabelecimentoEnum.UBS:
      return procedAutomaticoOdontoUbs(isTipoAtendimentoUrgencia)
    case TipoEstabelecimentoEnum.CEO:
      return isTipoAtendimentoUrgencia
        ? ProcedimentosAutomaticoAtendimentoOdonto.ATENDIMENTO_DE_URGENCIA_EM_ATENCAO_ESPECIALIZADA
        : ProcedimentosAutomaticoAtendimentoOdonto.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_ESPECIALIZADA
    case TipoEstabelecimentoEnum.POLICLINICA:
      return isDentista && isTipoAtendimentoUrgencia
        ? ProcedimentosAutomaticoAtendimentoOdonto.ATENDIMENTO_DE_URGENCIA_EM_ATENCAO_ESPECIALIZADA
        : ProcedimentosAutomaticoAtendimentoOdonto.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_ESPECIALIZADA
    case TipoEstabelecimentoEnum.UPA:
      return procedAutomaticoOdontoUpa(
        tipoConsulta,
        isTipoAtendimentoConsulta,
        isTipoAtendimentoUrgencia,
        isDentista,
        isDentistaClinicoGeral
      )
    default:
      return null
  }
}

const procedAutomaticoOdontoUbs = (isTipoAtendimentoUrgencia: boolean) => {
  return isTipoAtendimentoUrgencia
    ? ProcedimentosAutomaticosAtendimentoIndividual.ATENDIMENTO_DE_URGENCIA_NA_ATENCAO_BASICA
    : ProcedimentosAutomaticosAtendimentoIndividual.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_BASICA
}

const procedAutomaticoOdontoUpa = (
  tipoConsulta: TipoConsultaOdontoEnum | null | undefined,
  isTipoAtendimentoConsulta: boolean,
  isTipoAtendimentoUrgencia: boolean,
  isDentista: boolean,
  isDentistaClinicoGeral: boolean
):
  | ProcedimentosAutomaticoAtendimentoUpa
  | ProcedimentosAutomaticoAtendimentoOdonto
  | ProcedimentosAutomaticoAtendimentoPoliclinica
  | null => {
  const isTipoConsultaVazio = isUndefinedOrNull(tipoConsulta)
  const isConsultaManutencao = tipoConsulta === TipoConsultaOdontoEnum.CONSULTA_DE_MANUTENCAO
  const isConsultaManutencaoOuRetorno =
    isConsultaManutencao || tipoConsulta === TipoConsultaOdontoEnum.CONSULTA_DE_RETORNO

  if (isTipoAtendimentoConsulta) {
    if (!(isTipoConsultaVazio || isConsultaManutencaoOuRetorno)) return null
    if (isDentistaClinicoGeral) return ProcedimentosAutomaticoAtendimentoUpa.ATENDIMENTO_MEDICO_UPA
    if (isDentista) {
      return ProcedimentosAutomaticoAtendimentoOdonto.CONSULTA_DE_PROFISSIONAIS_DE_NIVEL_SUPERIOR_NA_ATENCAO_ESPECIALIZADA
    }
    return null
  }
  if (isTipoAtendimentoUrgencia && isDentista && (isTipoConsultaVazio || isConsultaManutencao)) {
    return ProcedimentosAutomaticoAtendimentoPoliclinica.ATENDIMENTO_DE_URGENCIA_EM_ATENCAO_ESPECIALIZADA
  }
  return null
}

export type ProcedimentosAutomaticosAtendimentos =
  | ProcedimentosAutomaticosAtendimentoIndividual
  | ProcedimentosAutomaticosEscutaInicial
  | ProcedimentosAutomaticoAtendimentoUpa

export function addProcedimentoAutomatico(
  listaProcedimentos: ReadonlyArray<ProcedimentoModel>,
  procedimentosAutomaticos: ReadonlyArray<ProcedimentoAutomatico>,
  codigoProcedimentoToAdd: ProcedimentosAutomaticosAtendimentos
): ProcedimentoModel[] {
  const newListaProcedimentos = isUndefinedOrNull(listaProcedimentos) ? [] : [...listaProcedimentos]
  const procedimentoAutomatico = procedimentosAutomaticos.find((item) => item.codigo === codigoProcedimentoToAdd)

  if (procedimentoAutomatico) {
    if (!newListaProcedimentos.some((p) => p.procedimento.id === procedimentoAutomatico.id)) {
      const procedimento: ProcedimentoModel = {
        _id: uuidv4(),
        procedimento: procedimentoAutomatico,
        automatico: true,
      }
      newListaProcedimentos.push(procedimento)
    }
  }
  return newListaProcedimentos
}

export function removeProcedimentoAutomatico(
  listaProcedimentos: ReadonlyArray<ProcedimentoModel>,
  codigoProcedimentoToRemove: ProcedimentosAutomaticosAtendimentos
): ProcedimentoModel[] | ReadonlyArray<ProcedimentoModel> {
  if (isUndefinedOrNull(listaProcedimentos)) {
    return listaProcedimentos
  }
  return listaProcedimentos.filter((p) => !p.automatico || p.procedimento.codigo !== codigoProcedimentoToRemove)
}

export function addOrRemoveAfericaoPressaoArterial(
  pressaoArterialSistolica: string,
  pressaoArterialDiastolica: string,
  listaProcedimentos: ReadonlyArray<ProcedimentoModel>,
  procedimentosAutomaticos: ReadonlyArray<ProcedimentoAutomatico>,
  codigosProcedimentosAutomaticosExcluidos: ReadonlyArray<string>,
  procedimentoAfericaoPressaoArterial:
    | ProcedimentosAutomaticosEscutaInicial.AFERICAO_PRESSAO_ARTERIAL
    | ProcedimentosAutomaticosAtendimentoIndividual.AFERICAO_PRESSAO_ARTERIAL
) {
  if (
    pressaoArterialSistolica &&
    pressaoArterialDiastolica &&
    shouldAddProcedimentoAutomatico(procedimentoAfericaoPressaoArterial, codigosProcedimentosAutomaticosExcluidos)
  ) {
    return addProcedimentoAutomatico(listaProcedimentos, procedimentosAutomaticos, procedimentoAfericaoPressaoArterial)
  }

  return removeProcedimentoAutomatico(listaProcedimentos, procedimentoAfericaoPressaoArterial)
}

export function addOrRemoveAfericaoTemperatura(
  temperatura: string,
  listaProcedimentos: ReadonlyArray<ProcedimentoModel>,
  procedimentosAutomaticos: ReadonlyArray<ProcedimentoAutomatico>,
  codigosProcedimentosAutomaticosExcluidos: ReadonlyArray<string>,
  procedimentoAfericaoTemperatura:
    | ProcedimentosAutomaticosEscutaInicial.AFERICAO_DE_TEMPERATURA
    | ProcedimentosAutomaticosAtendimentoIndividual.AFERICAO_DE_TEMPERATURA
) {
  if (
    temperatura &&
    shouldAddProcedimentoAutomatico(procedimentoAfericaoTemperatura, codigosProcedimentosAutomaticosExcluidos)
  ) {
    return addProcedimentoAutomatico(listaProcedimentos, procedimentosAutomaticos, procedimentoAfericaoTemperatura)
  }

  return removeProcedimentoAutomatico(listaProcedimentos, procedimentoAfericaoTemperatura)
}

export function addOrRemoveGlicemiaCapilar(
  glicemia: string,
  hasAccessProcedimentoGlicemiaCapilar: boolean,
  listaProcedimentos: ReadonlyArray<ProcedimentoModel>,
  procedimentosAutomaticos: ReadonlyArray<ProcedimentoAutomatico>,
  codigosProcedimentosAutomaticosExcluidos: ReadonlyArray<string>,
  procedimentoGlicemiaCapilar:
    | ProcedimentosAutomaticosEscutaInicial.GLICEMIA_CAPILAR
    | ProcedimentosAutomaticosAtendimentoIndividual.GLICEMIA_CAPILAR
) {
  if (
    hasAccessProcedimentoGlicemiaCapilar &&
    glicemia &&
    shouldAddProcedimentoAutomatico(procedimentoGlicemiaCapilar, codigosProcedimentosAutomaticosExcluidos)
  ) {
    return addProcedimentoAutomatico(listaProcedimentos, procedimentosAutomaticos, procedimentoGlicemiaCapilar)
  }

  return removeProcedimentoAutomatico(listaProcedimentos, procedimentoGlicemiaCapilar)
}

export function shouldAddProcedimentoAutomatico(
  codigoProcedimentoAutomatico: string,
  codigosProcedimentosAutomaticosExcluidos: ReadonlyArray<string>
) {
  if (isUndefinedOrNull(codigosProcedimentosAutomaticosExcluidos)) {
    return true
  }
  return !codigosProcedimentosAutomaticosExcluidos.includes(codigoProcedimentoAutomatico)
}
